import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike'
import DownhillSkiingIcon from '@mui/icons-material/DownhillSkiing'
import EventIcon from '@mui/icons-material/Event'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusy from '@mui/icons-material/EventBusy'
import EventNote from '@mui/icons-material/EventNote'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PoolIcon from '@mui/icons-material/Pool'
import SnowboardingIcon from '@mui/icons-material/Snowboarding'
import SnowshoeingIcon from '@mui/icons-material/Snowshoeing'

export const isHistoryPage = (location) => {
  return location.pathname === '/mysessions'
}

export const isBookingPage = (location) => {
  return location.pathname === '/booking'
}

export const isWeChatBrowser = () => {
  return /MicroMessenger/i.test(navigator.userAgent) || typeof window.WeixinJSBridge !== 'undefined'
}

// 备选的复制到剪贴板方法
export const fallbackCopyTextToClipboard = (text) => {
  try {
    // 创建一个临时文本区域元素
    const textArea = document.createElement('textarea')
    textArea.value = text

    // 设置样式使其在页面上不可见
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.width = '2em'
    textArea.style.height = '2em'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.outline = 'none'
    textArea.style.boxShadow = 'none'
    textArea.style.background = 'transparent'

    document.body.appendChild(textArea)

    // 针对iOS的特殊处理
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    if (isIOS) {
      // iOS需要特殊处理才能进行选择
      const range = document.createRange()
      range.selectNodeContents(textArea)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      textArea.setSelectionRange(0, 999999)
    } else {
      textArea.focus()
      textArea.select()
    }

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (successful) {
      alert('Link copied to clipboard!!')
    } else {
      alert('Please copy this link manually: ' + text)
    }
  } catch (err) {
    console.error('Fallback: Could not copy text: ', err)
    alert('Please copy this link manually: ' + text)
  }
}

// 统一的剪贴板复制功能
export const copyToClipboard = (text) => {
  // 检查现代剪贴板API是否可用
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert('Link copied to clipboard!')
      })
      .catch((_err) => {
        fallbackCopyTextToClipboard(text)
      })
  } else {
    fallbackCopyTextToClipboard(text)
  }
}

const SPORTS = {
  SNOWBOARD: { value: 'SNOWBOARD', label: 'Snowboard', icon: <SnowboardingIcon /> },
  SKI: { value: 'SKI', label: 'Ski', icon: <DownhillSkiingIcon /> },
  MOUNTAIN_BIKE: { value: 'MOUNTAIN_BIKE', label: 'Mountain Bike', icon: <DirectionsBikeIcon /> },
  WATER_SPORTS: { value: 'WATER_SPORTS', label: 'Water Sports', icon: <PoolIcon /> },
  OTHERS: { value: 'OTHERS', label: 'Others', icon: <MoreHorizIcon /> },
  DEFAULT: { value: 'DEFAULT', label: 'Default', icon: <SnowshoeingIcon /> },
}

export const getSportProps = (sport) => {
  if (!sport) return null
  return SPORTS[sport.toUpperCase()] || { ...SPORTS.DEFAULT, label: sport }
}

const STATUS_TYPES = {
  AVAILABLE: {
    color: 'success',
    icon: <EventAvailableIcon />,
    label: 'Available',
    id: 'AVAILABLE',
  },
  DRAFT: {
    color: 'warning',
    icon: <EventIcon />,
    label: 'Draft',
    id: 'DRAFT',
  },
  BOOKED: {
    color: 'info',
    icon: <EventIcon />,
    label: 'Booked',
    id: 'BOOKED',
  },
  FULL: {
    color: 'warning',
    icon: <EventIcon />,
    label: 'Full',
    id: 'FULL',
  },
  CANCELLED: {
    color: 'error',
    icon: <EventBusy />,
    label: 'Cancelled',
    id: 'CANCELLED',
  },
  COMPLETED: {
    color: 'primary',
    icon: <EventNote />,
    label: 'Completed',
    id: 'COMPLETED',
  },
  DEFAULT: {
    color: 'default',
    icon: <EventIcon />,
    id: 'DEFAULT',
  },
}

/**
 * 获取状态类型对象
 * @param {string} status - 状态值
 * @returns {Object} 包含颜色、图标和标签的状态类型对象
 */
export const getStatusProps = (status) => {
  // 如果没有传入状态，返回默认状态
  if (!status) {
    return { ...STATUS_TYPES.DEFAULT, label: '' }
  }

  // 从预定义状态类型中查找
  return STATUS_TYPES[status.toUpperCase()] || { ...STATUS_TYPES.DEFAULT, label: status }
}

export const VIEW_TYPES = {
  USER: 'user',
  INSTRUCTOR: 'instructor',
}

/**
 * Silently copy text to clipboard without showing alert dialogs.
 * Returns a Promise that resolves to true/false indicating success.
 */
export const copyTextToClipboardSilent = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (_) {
    // fall back to manual method
  }

  try {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.width = '2em'
    textArea.style.height = '2em'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.outline = 'none'
    textArea.style.boxShadow = 'none'
    textArea.style.background = 'transparent'
    document.body.appendChild(textArea)

    // iOS handling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    if (isIOS) {
      const range = document.createRange()
      range.selectNodeContents(textArea)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      textArea.setSelectionRange(0, 999999)
    } else {
      textArea.focus()
      textArea.select()
    }

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)
    return successful
  } catch (_) {
    return false
  }
}

/**
 * Validate North American phone numbers (NANP - North American Numbering Plan):
 * - Accepts optional country code 1 or +1
 * - Allows spaces, dashes, dots, and parentheses
 * - Ensures 10 digits after optional country code
 * - Area code and exchange code must start with 2-9
 * - Covers US, Canada, and other NANP countries
 */
export const isValidNorthAmericanPhoneNumber = (rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') {
    return false
  }

  let digits = rawInput.replace(/\D/g, '')

  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }

  if (digits.length !== 10) {
    return false
  }

  const areaFirst = digits.charAt(0)
  const exchangeFirst = digits.charAt(3)

  if (areaFirst < '2' || exchangeFirst < '2') {
    return false
  }

  return true
}

/**
 * Format a valid North American phone number to a standard readable format: XXX-XXX-XXXX
 * - Removes non-digits
 * - Strips leading country code 1
 * - Returns the input trimmed if not a valid 10-digit NANP number
 */
export const formatNorthAmericanPhoneNumber = (rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') {
    return rawInput
  }
  let digits = rawInput.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  if (digits.length !== 10) {
    return rawInput.trim()
  }
  const area = digits.slice(0, 3)
  const exch = digits.slice(3, 6)
  const line = digits.slice(6)
  return `${area}-${exch}-${line}`
}

/**
 * Build a tel: href from a phone number input. For valid North American numbers,
 * returns tel:+1XXXXXXXXXX; otherwise falls back to raw tel:<trimmedInput>.
 */
export const toTelHref = (rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') {
    return 'tel:'
  }
  const trimmed = rawInput.trim()
  let digits = trimmed.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }
  if (digits.length === 10 && isValidNorthAmericanPhoneNumber(trimmed)) {
    return `tel:+1${digits}`
  }
  return `tel:${trimmed}`
}

/**
 * Validate a WeChat ID based on common rules:
 * - 6–20 characters
 * - Starts with a letter
 * - Contains only letters, digits, underscore (_) or hyphen (-)
 * - No spaces or other special characters
 */
export const isValidWeChatId = (rawInput) => {
  if (!rawInput || typeof rawInput !== 'string') {
    return false
  }
  const trimmed = rawInput.trim()
  const re = /^[a-zA-Z][a-zA-Z0-9_-]{5,19}$/
  return re.test(trimmed)
}

// Backward compatibility aliases
export const isValidCanadianPhoneNumber = isValidNorthAmericanPhoneNumber
export const formatCanadianPhoneNumber = formatNorthAmericanPhoneNumber
